import { ReactNode } from "react";
import { AreaAccess, Capability } from "../services/access";

const CAPABILITY_LABEL: Record<Capability, string> = {
  view: "View",
  edit: "Edit",
};

/**
 * The toast for a saved access change. One sentence, the same shape every time:
 *
 *   {Role}'s {View and Edit | View | Edit} access to the {Area} route is
 *   {granted | revoked}.
 *
 * The capabilities named are the ones that ACTUALLY changed, not the one that
 * was clicked — "Edit implies View" means granting Edit can grant View too, and
 * revoking View revokes Edit. Both always move the same way, so one verb fits.
 *
 * `scope` names what was clicked rather than what was written. A group switch
 * writes one row per page underneath it, but the operator flipped Users, not
 * Decorator Links, and a toast naming a page they never touched reads as the
 * wrong thing having changed.
 */
export function describeAccessChange(input: {
  roleLabel: string;
  areaLabel: string;
  before: AreaAccess | undefined;
  after: AreaAccess;
  granted: boolean;
  scope?: "route" | "group";
}): { title: string; description: ReactNode } {
  const changed = (["view", "edit"] as Capability[]).filter(
    (c) => (input.before?.[c] ?? false) !== input.after[c],
  );

  const names = changed.map((c, i) => (
    <span key={c}>
      {i > 0 && " and "}
      <u>{CAPABILITY_LABEL[c]}</u>
    </span>
  ));

  return {
    title: "Access updated",
    description: (
      <span>
        {input.roleLabel}&rsquo;s {names} access to the {input.areaLabel}{" "}
        {input.scope === "group" ? "group" : "route"} is{" "}
        {input.granted ? "granted" : "revoked"}.
      </span>
    ),
  };
}
